import type { ChangeEvent, FC, ReactNode } from 'react'
import type { Pillar, PostCard as PostCardType } from '@/types'

import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { PostCard } from '@/components/content/post-card'

interface NavGroupArticlesProps {
    posts: PostCardType[]
    pillars: Pillar[]
    className?: string
}

const ALL = 'all'

// Every article in the nav group, with a topic filter that narrows the list to
// one pillar. Filtering happens in the browser so it is instant, and the full
// set is server-rendered, so every article is still linked without JavaScript.
export const NavGroupArticles: FC<NavGroupArticlesProps> = ({
    posts,
    pillars,
    className
}): ReactNode => {
    const [topic, setTopic] = useState<string>(ALL)

    const counts = useMemo(() => {
        const map = new Map<string, number>()
        for (const post of posts) {
            map.set(post.pillarSlug, (map.get(post.pillarSlug) ?? 0) + 1)
        }
        return map
    }, [posts])

    const visible = useMemo(
        () => (topic === ALL ? posts : posts.filter((post) => post.pillarSlug === topic)),
        [posts, topic]
    )

    const activePillar = pillars.find((pillar) => pillar.slug === topic) ?? null

    const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        setTopic(event.target.value)
    }

    return (
        <div className={cn('flex flex-col', className)}>
            <div className='flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between'>
                <div>
                    <h2 className='font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                        All articles
                    </h2>
                    <p aria-live='polite' className='mt-2 text-sm text-muted-foreground'>
                        {visible.length} {visible.length === 1 ? 'article' : 'articles'}
                        {activePillar ? (
                            <>
                                {' in '}
                                <span data-mdlfy-cms={`pillars:${activePillar.id}:name`}>
                                    {activePillar.name}
                                </span>
                            </>
                        ) : (
                            ' across every topic here'
                        )}
                    </p>
                </div>

                <div className='relative w-full sm:w-72'>
                    <label htmlFor='topic-filter' className='sr-only'>
                        Filter articles by topic
                    </label>

                    <select
                        id='topic-filter'
                        value={topic}
                        onChange={handleChange}
                        className='w-full cursor-pointer appearance-none rounded-full border border-border bg-card py-2.5 pl-5 pr-11 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/50 focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/50'
                    >
                        <option value={ALL}>All topics ({posts.length})</option>

                        {pillars.map((pillar) => (
                            <option key={pillar.id} value={pillar.slug}>
                                {pillar.name} ({counts.get(pillar.slug) ?? 0})
                            </option>
                        ))}
                    </select>

                    <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                </div>
            </div>

            {visible.length > 0 ? (
                <div
                    key={topic}
                    className='mt-12 grid animate-in grid-cols-1 gap-x-8 gap-y-12 fade-in duration-500 motion-reduce:animate-none sm:grid-cols-2 lg:grid-cols-3'
                >
                    {visible.map((post) => (
                        <PostCard key={post.id} post={post} />
                    ))}
                </div>
            ) : (
                <div className='mt-12 rounded-2xl border border-dashed border-border bg-card/50 px-6 py-16 text-center'>
                    <p className='font-heading text-lg font-semibold text-foreground'>
                        Nothing published here yet
                    </p>
                    <p className='mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
                        {activePillar ? (
                            <>
                                {'Articles for '}
                                <span data-mdlfy-cms={`pillars:${activePillar.id}:name`}>
                                    {activePillar.name}
                                </span>
                                {' are still being written. The guide itself is ready to read in the meantime.'}
                            </>
                        ) : (
                            'Articles for this section are on the way.'
                        )}
                    </p>

                    <button
                        type='button'
                        onClick={() => setTopic(ALL)}
                        className='mt-6 inline-flex items-center rounded-full border border-border bg-card px-5 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:text-primary'
                    >
                        Show every topic
                    </button>
                </div>
            )}
        </div>
    )
}
